import { Suspense, type JSX } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import { EptMarkLoader } from '@eptai/eptai-app-components';
import { lazyWithRetry } from './utils/lazyWithRetry';
import AdminRoleGuard from './components/common/AdminRoleGuard';
import FeatureFlagGuard from './components/common/FeatureFlagGuard';
import RoomsAccessGuard from './components/common/RoomsAccessGuard';
import { useAppAuth, useFeatureFlags } from './contexts';
import Login from './pages/Login';
import SignedOut from './pages/SignedOut';
import TrainingAuthSync from './pages/TrainingAuthSync';
import { isAppLoginPath } from './utils/appLogin';
import { WORKSPACE_ENABLED_FLAG, WORKSPACE_ROUTE } from './utils/appRoutes';
import {
  controlIssuerForHost,
  controlLoginPath,
  peekMcpControlLogin,
  readMcpControlLogin,
} from './utils/mcpControlLogin';
import { CHARACTERIZATION_ENABLED_FLAG } from './utils/characterizationHelpers';
import {
  getSalesforceEmbeddedRoomPath,
  isAllowedSalesforceEmbeddedPath,
  rememberSalesforceEmbeddedRoomPath,
} from './utils/salesforceEmbeddedNavigation';
import { isSalesforceEmbedded } from './config';

/** Full-viewport loader for auth/flags bootstrap and route Suspense. */
const PageLoader = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      width: '100%',
    }}
  >
    <EptMarkLoader size={56} ariaLabel="Loading" />
  </div>
);

// Allowlist of trusted messages the OAuth install/connect callbacks may redirect
// to `/?message=` with. Only these are forwarded to the connections toast, so an
// attacker cannot use `/?message=<arbitrary text>` to spoof a trusted notification.
const ALLOWED_INTEGRATION_MESSAGES = new Set([
  'Successfully installed integration',
  'Outlook connected successfully',
]);

export function getIntegrationSuccessRedirectPath(search: string): string | null {
  const params = new URLSearchParams(search);
  const rawMessage = params.get('message');
  const message = rawMessage?.replace(/^"|"$/g, '').trim();
  if (!message || !ALLOWED_INTEGRATION_MESSAGES.has(message)) {
    return null;
  }
  if (message === 'Outlook connected successfully') {
    return `/user-profile/connections?message=${encodeURIComponent(message)}`;
  }
  const integrationId = (params.get('integration_id') || '').trim();
  if (/^[0-9a-zA-Z_-]{8,128}$/.test(integrationId)) {
    const qs = new URLSearchParams({
      message,
      sharepoint_sites: '1',
    });
    return `/integrations/${integrationId}?${qs.toString()}`;
  }
  return `/integrations?message=${encodeURIComponent(message)}`;
}

/**
 * AI Sales Pro users should open the app on the rooms list, not AI Chat.
 *
 * Uses the Auth0 role as assigned (not EptAdmin's expanded sidebar roles), so
 * superadmins keep the AI Chat home.
 *
 * Example: `['AI Sales Pro']` -> true; `['Admin']` / `['EptAdmin']` -> false.
 */
export function shouldLandOnRoomsList(
  roles: readonly string[] | null | undefined
): boolean {
  return Boolean(roles?.includes('AI Sales Pro'));
}

/**
 * Landing URL for AI Sales Pro users: the rooms list pre-sorted by the rooms
 * they opened most recently (the list's own default sort).
 */
export const ROOMS_LANDING_PATH = '/application-rooms?order_by=user_last_opened_at&sort=DESC';

/**
 * Landing URL for `/`.
 *
 * Integration success toasts win first. When `workspace_enabled` is on, every
 * role lands on `/workspace`. Otherwise AI Sales Pro goes to the rooms list
 * and other roles go to `/ai-chat`.
 *
 * Example: `workspaceEnabled=true` -> `/workspace`;
 * `workspaceEnabled=false` + `['AI Sales Pro']` -> rooms list.
 */
export function getHomeRedirectPath(
  search: string,
  roles: readonly string[] | null | undefined,
  workspaceEnabled = false
): string {
  const integrationRedirectPath = getIntegrationSuccessRedirectPath(search);
  if (integrationRedirectPath) {
    return integrationRedirectPath;
  }

  if (workspaceEnabled) {
    return WORKSPACE_ROUTE;
  }

  if (shouldLandOnRoomsList(roles)) {
    return ROOMS_LANDING_PATH;
  }

  return '/ai-chat';
}

/**
 * Paths whose destination is decided in `getHomeRedirectPath` (workspace flag
 * or role), so a saved pre-login path must never override them.
 */
const ROLE_LANDING_PATHS = new Set(['/', '/ai-chat', '/application-rooms', WORKSPACE_ROUTE]);

/**
 * True when this login URL is the MCP connect return for this app host.
 *
 * Example input: `/login?mcp_txn=abc&iss=https://mcp.ept.ai/...` and `app.ept.ai`.
 * Example output: true. `/login` alone, or another issuer, is false.
 */
function _isControlLoginReturn(redirectPath: string, hostname: string): boolean {
  const queryIndex = redirectPath.indexOf('?');
  if (queryIndex < 0) {
    return false;
  }
  const pathname = redirectPath.slice(0, queryIndex).replace(/\/+$/, '') || '/';
  if (pathname !== '/login') {
    return false;
  }
  return (
    readMcpControlLogin(redirectPath.slice(queryIndex), controlIssuerForHost(hostname)) !== null
  );
}

/**
 * Whether the path saved before login (`redirectPath`) should be restored.
 *
 * Only real deep links qualify. `ForumListContext` saves whatever page the user
 * was on when the session lapsed, so an AI Sales Pro user bounced off `/ai-chat`
 * would be sent straight back there after login — overriding their role landing.
 *
 * Also rejects anything that is not a same-origin path, since the value comes
 * from localStorage and is handed to `window.location.replace`. An MCP connect
 * return to `/login` is kept, because that page still has to finish the code.
 *
 * Example: `/room/42` -> true; `/ai-chat` -> false; `//evil.com` -> false.
 */
export function shouldRestoreDeepLinkAfterLogin(
  redirectPath: string | null | undefined,
  hostname: string = typeof window === 'undefined' ? '' : window.location.hostname
): redirectPath is string {
  if (!redirectPath || !redirectPath.startsWith('/') || redirectPath.startsWith('//')) {
    return false;
  }
  if (_isControlLoginReturn(redirectPath, hostname)) {
    return true;
  }
  if (isAppLoginPath(redirectPath)) {
    return false;
  }
  const pathname = redirectPath.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  return !ROLE_LANDING_PATHS.has(pathname);
}

/**
 * Pick the post-Auth0 navigation target.
 *
 * Prefers Auth0 `appState.returnTo` (same-origin deep links only), then the
 * path ForumListContext saved in localStorage. Role landing paths stay with
 * HomeRoute.
 *
 * Example: appState `/training-auth-sync?returnTo=...` -> that path;
 * localStorage `/login` with no appState -> `/login`; absolute URL -> null.
 */
export function resolvePostLoginRedirect(
  appStateReturnTo: string | null | undefined,
  savedRedirectPath: string | null | undefined,
  currentPathname: string
): string | null {
  if (shouldRestoreDeepLinkAfterLogin(appStateReturnTo)) {
    return appStateReturnTo;
  }
  if (
    shouldRestoreDeepLinkAfterLogin(savedRedirectPath) &&
    savedRedirectPath !== currentPathname
  ) {
    return savedRedirectPath;
  }
  return null;
}

/**
 * Where to send users who hit `/application-rooms` without rooms access.
 *
 * Must NOT be `/` — that re-enters HomeRoute and can bounce AI Sales Pro
 * users off the rooms list onto `/ai-chat` when roles are briefly unset.
 *
 * Example: denied rooms access -> `/ai-chat` (stays off `/`).
 */
export const ROOMS_ACCESS_DENIED_REDIRECT = '/ai-chat';

export function HomeRoute(): JSX.Element {
  const location = useLocation();
  const { user, isLoading } = useAppAuth();
  const { hasFlag, isLoading: flagsLoading } = useFeatureFlags();
  const integrationRedirectPath = getIntegrationSuccessRedirectPath(location.search);

  if (integrationRedirectPath) {
    return <Navigate replace to={integrationRedirectPath} />;
  }

  if (isLoading || flagsLoading) {
    return <PageLoader />;
  }

  const pendingControlLogin = user ? peekMcpControlLogin() : null;
  if (pendingControlLogin) {
    return <Navigate replace to={controlLoginPath(pendingControlLogin)} />;
  }

  const roles = (user as { ept_roles?: string[] } | undefined)?.ept_roles;
  return (
    <Navigate
      replace
      to={getHomeRedirectPath(location.search, roles, hasFlag(WORKSPACE_ENABLED_FLAG))}
    />
  );
}

function SalesforceEmbeddedRouteBlocker(): JSX.Element {
  const location = useLocation();
  const embeddedRoomPath = getSalesforceEmbeddedRoomPath();
  const currentUrl =
    typeof window === 'undefined'
      ? `${location.pathname}${location.search}${location.hash}`
      : `${window.location.origin}${location.pathname}${location.search}${location.hash}`;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: '24px',
        backgroundColor: '#f8fafc',
      }}
    >
      <div
        style={{
          maxWidth: '560px',
          padding: '24px',
          borderRadius: '12px',
          backgroundColor: '#ffffff',
          boxShadow: '0 8px 30px rgba(15, 23, 42, 0.08)',
          textAlign: 'center',
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: '12px', color: '#0f172a' }}>
          This page is not available inside Salesforce
        </h2>
        <p style={{ margin: '0 0 20px', color: '#475569', lineHeight: 1.5 }}>
          Salesforce embeds are limited to deal room pages. You can continue in a new browser tab.
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <a
            href={currentUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'inline-block',
              padding: '10px 16px',
              borderRadius: '8px',
              backgroundColor: '#053f98',
              color: '#ffffff',
              textDecoration: 'none',
              fontWeight: 600,
            }}
          >
            Open this page in a new tab
          </a>
          {embeddedRoomPath ? (
            <a
              href={embeddedRoomPath}
              style={{
                display: 'inline-block',
                padding: '10px 16px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                color: '#0f172a',
                textDecoration: 'none',
                fontWeight: 600,
              }}
            >
              Return to deal room
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function SalesforceEmbeddedRouteGuard({
  children,
}: {
  children: JSX.Element;
}): JSX.Element {
  const location = useLocation();
  const embedded = isSalesforceEmbedded();
  const embeddedRoomPath = rememberSalesforceEmbeddedRoomPath();
  const allowed = isAllowedSalesforceEmbeddedPath(location.pathname);

  console.log('[SALESFORCE CANVAS][ROUTER DEBUG]', {
    href: typeof window === 'undefined' ? location.pathname : window.location.href,
    pathname: location.pathname,
    search: location.search,
    embeddedRoomPath,
    embedded,
    allowed,
  });

  if (!embedded) {
    return children;
  }

  if (allowed) {
    return children;
  }

  console.warn('[SALESFORCE CANVAS][ROUTER DEBUG] Blocking embedded route', location.pathname);
  return <SalesforceEmbeddedRouteBlocker />;
}

// Lazy load all page components using lazyWithRetry for chunk loading resilience
// Core pages (loaded early but still lazy for code splitting)
const AiChatPage = lazyWithRetry(() => import('./pages/AiChatPage'));
const MainContent = lazyWithRetry(() => import('./pages/MainContent'));
const Page404 = lazyWithRetry(() => import('./pages/Page404'));

/**
 * Whether a `/room/:dealroom_id` path segment can name a room.
 *
 * Room ids are integers everywhere they are produced, so anything else is a
 * malformed URL. `/room/entity_data` reached production and mounted the room
 * page on the literal string, which then fired every room-scoped call with it:
 * `/v3/deal_room/core/entity_data` came back 400 and
 * `/v3/deal_room_members/entity_data` 500'd on `int()`.
 *
 * Example: `"18712"` -> `true`; `"entity_data"`, `""`, `undefined` -> `false`.
 */
export const isRoomIdPathParam = (value: string | undefined): boolean =>
  /^\d+$/.test(value ?? '');

/**
 * One mount decision, rather than a guard in each of the room page's fetches.
 * This is also the only place that knows the value is a route param and not an
 * id somebody already validated.
 */
const DealRoomRoute: React.FC = () => {
  const { dealroom_id } = useParams<{ dealroom_id: string }>();
  return isRoomIdPathParam(dealroom_id) ? (
    <DealRoomV3 id="deal-room-detail" />
  ) : (
    <Page404 />
  );
};
const WelcomeUser = lazyWithRetry(() => import('./pages/WelcomeUser'));

// Auth & Account pages
const NewPassword = lazyWithRetry(() => import('./pages/NewPassword'));
const FreeTrial = lazyWithRetry(() => import('./pages/FreeTrial'));
const Pricing = lazyWithRetry(() => import('./pages/Pricing'));
const UserProfilePage = lazyWithRetry(() => import('./pages/UserProfilePage'));
const NotificationsPage = lazyWithRetry(() => import('./pages/NotificationsPage'));
const EmailPreferencesPage = lazyWithRetry(() => import('./pages/EmailPreferencesPage'));

// Settings pages
const Settings = lazyWithRetry(() => import('./pages/Settings'));
const DownloadFile = lazyWithRetry(() => import('./components/settings/partials/DownloadFile'));
const GkdEditor = lazyWithRetry(() => import('./components/settings/partials/GkdEditor.tsx'));
const BrainDump = lazyWithRetry(() => import('./components/settings/partials/BrainDump'));
const CannedResponseEditor = lazyWithRetry(
  () => import('./components/settings/partials/CannedResponseEditor.tsx')
);
const AnonymizedTicketEditor = lazyWithRetry(
  () => import('./components/settings/partials/AnonymizedTicketEditor')
);
const DownloadKsItem = lazyWithRetry(() => import('./components/settings/partials/DownloadKsItem'));

// Response & Conversation pages
const ResponseListNew = lazyWithRetry(() => import('./pages/ResponseListNew'));
const ResponseDetailsPage = lazyWithRetry(() => import('./pages/ResponseDetailsPage'));
const ConversationsNew = lazyWithRetry(() => import('./pages/ConversationsNew'));
const ConversationDetailPage = lazyWithRetry(() => import('./pages/ConversationDetailPage'));
const ReportingNew = lazyWithRetry(() => import('./pages/ReportingNew'));

// Forum pages
const Threads = lazyWithRetry(() => import('./components/dashboard/Threads'));

// Sales pages
const SalesGuideV2 = lazyWithRetry(() => import('./pages/SalesGuideV2'));
const CreateSalesGuide = lazyWithRetry(() => import('./components/salesGuide/CreateSalesGuide'));
const SalesPlaybook = lazyWithRetry(() => import('./pages/SalesPlaybook'));
const CreateSalesPlaybook = lazyWithRetry(
  () => import('./components/salesPlaybook/CreateSalesPlaybook')
);

// Entity pages
const ApplicationRoomsList = lazyWithRetry(() => import('./pages/ApplicationRoomsList'));
const AiGuidanceList = lazyWithRetry(() => import('./pages/AiGuidanceList'));
const AiGuidanceCreatePage = lazyWithRetry(() => import('./pages/AiGuidanceCreatePage'));
const AiGuidanceDetailPage = lazyWithRetry(() => import('./pages/AiGuidanceDetailPage'));
const CharacterizationsList = lazyWithRetry(() => import('./pages/CharacterizationsList'));
const CharacterizationDetailPage = lazyWithRetry(
  () => import('./pages/CharacterizationDetailPage')
);
const SkillsList = lazyWithRetry(() => import('./pages/SkillsList'));
const SkillsCreatePage = lazyWithRetry(() => import('./pages/SkillsCreatePage'));
const SkillsDetailPage = lazyWithRetry(() => import('./pages/SkillsDetailPage'));

// Deal Room pages
const DealRoomsList = lazyWithRetry(() => import('./pages/DealRoomsList'));
const DealRoomV3 = lazyWithRetry(() => import('./pages/DealRoomV3'));
const DownloadDealRoom = lazyWithRetry(() => import('./components/dealroom/DownloadDealRoom'));
const AddEmailToDealRoom = lazyWithRetry(() => import('./pages/AddEmailToDealRoom'));
const CanvasDownloadRedirect = lazyWithRetry(() => import('./pages/CanvasDownloadRedirect'));
const DocumentRedirect = lazyWithRetry(() => import('./pages/DocumentRedirect'));
const EmailSubscriptionAction = lazyWithRetry(() => import('./pages/EmailSubscriptionAction'));
const RemoveResourceAction = lazyWithRetry(() => import('./pages/RemoveResourceAction'));
const DealRoomRouteRedirect = lazyWithRetry(() => import('./pages/DealRoomRouteRedirect'));

// Admin pages
const Users = lazyWithRetry(() => import('./pages/Users'));
const ChannelList = lazyWithRetry(() => import('./pages/ChannelList'));
const ChannelDetailsPage = lazyWithRetry(() => import('./pages/ChannelDetailsPage'));
const KnowledgeSourceList = lazyWithRetry(() => import('./pages/KnowledgeSourceList'));
const KnowledgeSourceDetailsPage = lazyWithRetry(
  () => import('./pages/KnowledgeSourceDetailsPage')
);
const KnowledgeSourceConfigurationsList = lazyWithRetry(
  () => import('./pages/KnowledgeSourceConfigurationsList')
);
const KnowledgeSourceConfigurationDetailsPage = lazyWithRetry(
  () => import('./pages/KnowledgeSourceConfigurationDetailsPage')
);
const IntegrationsList = lazyWithRetry(() => import('./pages/IntegrationsList'));
const IntegrationsBrowsePage = lazyWithRetry(() => import('./pages/IntegrationsBrowsePage'));
const IntegrationsApiKeysPage = lazyWithRetry(() => import('./pages/IntegrationsApiKeysPage'));
const IntegrationDetailsPage = lazyWithRetry(() => import('./pages/IntegrationDetailsPage'));
const CreateChannelPage = lazyWithRetry(() => import('./pages/CreateChannelPage'));
const CreateKnowledgeSourcePage = lazyWithRetry(
  () => import('./pages/CreateKnowledgeSourcePage')
);
const CreateKscPage = lazyWithRetry(() => import('./pages/CreateKscPage'));

// Ghost/placeholder pages
const GostPages = lazyWithRetry(() => import('./pages/GostPages'));
const DevErrorBoundaryPage =
  process.env.NODE_ENV === 'development'
    ? lazyWithRetry(() => import('./pages/DevErrorBoundaryPage'))
    : null;

function Router(): JSX.Element {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <SalesforceEmbeddedRouteGuard>
          <Routes>
            {DevErrorBoundaryPage ? (
              <Route path="/dev/error-boundary" element={<DevErrorBoundaryPage />} />
            ) : null}
            <Route path="/new-password" element={<NewPassword />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route
              path="/settings"
              element={<Navigate replace to="/settings/account-management" />}
            />
            <Route path="/settings/:tab" element={<Settings />} />
            <Route path="/settings/email-preferences" element={<EmailPreferencesPage />} />

          <Route path="/settings/ks/item/download/" element={<DownloadFile />} />
          <Route path="/:ks_id/item/:item_url" element={<DownloadKsItem />} />
          <Route path="/settings/ks/:ks_id/:filename" element={<GkdEditor />} />
          <Route path="/settings/ks/:ks_id/new-gkd/" element={<BrainDump />} />

          <Route
            path="/settings/ks/canned-response/:canned_response_id"
            element={<CannedResponseEditor />}
          />
          <Route
            path="/settings/ks/anonymized-tickets/:anonymized_ticket_id"
            element={<AnonymizedTicketEditor />}
          />
          <Route path="/responses" element={<ResponseListNew />} />
          <Route path="/response/:response_id" element={<ResponseDetailsPage />} />
          <Route path="/free-trial" element={<FreeTrial />} />
          <Route path="/reporting" element={<ReportingNew />} />
          {/* <Route path="/reporting-new" element={<ReportingNew />} /> */}
          <Route path="/community-forum/:forumid/page/:pagenumber" element={<MainContent />} />
          <Route path="/thread_id/:threadid" element={<Threads />} />

          <Route path="/question-type" element={<GostPages />} />
          <Route path="/ai-voice" element={<GostPages />} />

          <Route index element={<HomeRoute />} />
          <Route path="/" element={<HomeRoute />} />
          <Route path="/ai-chat" element={<AiChatPage id="ai-chat-home" />} />
          <Route
            path={WORKSPACE_ROUTE}
            element={
              <FeatureFlagGuard flagKey={WORKSPACE_ENABLED_FLAG} redirectTo="/ai-chat">
                <AiChatPage id="ai-chat-workspace" layout="home" />
              </FeatureFlagGuard>
            }
          />
          <Route path="/welcome" element={<WelcomeUser />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signed-out" element={<SignedOut />} />
          <Route path="/training-auth-sync" element={<TrainingAuthSync />} />
          <Route path="*" element={<Page404 />} />
          <Route path="404" element={<Page404 />} />
          <Route path="/conversations" element={<ConversationsNew />} />
          <Route path="/conversation/:conversation_id" element={<ConversationDetailPage />} />

          <Route path="/sales-playbook" element={<SalesPlaybook />} />
          <Route path="/sales-playbook/create" element={<CreateSalesPlaybook />} />
          <Route path="/sales-playbook/create/:playbook_id/" element={<CreateSalesPlaybook />} />

          <Route path="/sales-guide" element={<SalesGuideV2 />} />
          <Route path="/sales-guide/create" element={<CreateSalesGuide />} />
          <Route path="/sales-guide/create/:guide_id" element={<CreateSalesGuide />} />

          <Route
            path="/application-rooms"
            element={
              <RoomsAccessGuard redirectTo={ROOMS_ACCESS_DENIED_REDIRECT}>
                <ApplicationRoomsList />
              </RoomsAccessGuard>
            }
          />
          <Route
            path="/ai-guidance"
            element={
              <AdminRoleGuard redirectTo="/">
                <AiGuidanceList />
              </AdminRoleGuard>
            }
          />
          <Route
            path="/ai-guidance/create"
            element={
              <AdminRoleGuard redirectTo="/">
                <AiGuidanceCreatePage />
              </AdminRoleGuard>
            }
          />
          <Route
            path="/ai-guidance/:guidance_id"
            element={
              <AdminRoleGuard redirectTo="/">
                <AiGuidanceDetailPage />
              </AdminRoleGuard>
            }
          />
          <Route
            path="/characterizations"
            element={
              <FeatureFlagGuard flagKey={CHARACTERIZATION_ENABLED_FLAG} redirectTo="/">
                <AdminRoleGuard redirectTo="/">
                  <CharacterizationsList />
                </AdminRoleGuard>
              </FeatureFlagGuard>
            }
          />
          <Route
            path="/characterizations/:characterization_id"
            element={
              <FeatureFlagGuard flagKey={CHARACTERIZATION_ENABLED_FLAG} redirectTo="/">
                <AdminRoleGuard redirectTo="/">
                  <CharacterizationDetailPage />
                </AdminRoleGuard>
              </FeatureFlagGuard>
            }
          />
          <Route
            path="/skills"
            element={
              <FeatureFlagGuard flagKey="skills_enabled" redirectTo="/">
                <AdminRoleGuard redirectTo="/">
                  <SkillsList />
                </AdminRoleGuard>
              </FeatureFlagGuard>
            }
          />
          <Route
            path="/skills/new"
            element={
              <FeatureFlagGuard flagKey="skills_enabled" redirectTo="/">
                <AdminRoleGuard redirectTo="/">
                  <SkillsCreatePage />
                </AdminRoleGuard>
              </FeatureFlagGuard>
            }
          />
          <Route
            path="/skills/:name"
            element={
              <FeatureFlagGuard flagKey="skills_enabled" redirectTo="/">
                <AdminRoleGuard redirectTo="/">
                  <SkillsDetailPage />
                </AdminRoleGuard>
              </FeatureFlagGuard>
            }
          />
          <Route path="/deal-rooms" element={<DealRoomsList />} />
          <Route path="/room/:dealroom_id" element={<DealRoomRoute />} />
          <Route path="/deal_room_unsubscribe/:dealroom_id" element={<EmailSubscriptionAction />} />
          <Route path="/room/remove_resource" element={<RemoveResourceAction />} />
          <Route path="/canvas-download/:canvasId/:fileType" element={<CanvasDownloadRedirect />} />
          <Route path="/documents/*" element={<DocumentRedirect />} />

          <Route
            path="/room/download/:dealroom_id/:resource_id"
            element={<DownloadDealRoom />}
          />
          <Route
            path="/room/add-email-to-deal-room/:dealroom_id/:filename"
            element={<AddEmailToDealRoom />}
          />

          {/* Backwards-compatibility: redirect old /deal-room/* URLs to /room/*
              while preserving the rest of the path, query string, and hash. */}
          <Route path="/deal-room/*" element={<DealRoomRouteRedirect />} />

          <Route path="/users" element={<Users />} />
          <Route path="/user-profile" element={<UserProfilePage />} />
          <Route path="/user-profile/:profile_tab" element={<UserProfilePage />} />
          <Route path="/notifications" element={<NotificationsPage />} />

          <Route path="/channels" element={<ChannelList />} />
          <Route path="/channels/create" element={<CreateChannelPage />} />
          <Route path="/channels/:channel_id" element={<ChannelDetailsPage />} />
          <Route path="/settings/channel/:channel_id" element={<ChannelDetailsPage />} />

          <Route path="/knowledge-sources" element={<KnowledgeSourceList />} />
          <Route path="/knowledge-sources/create" element={<CreateKnowledgeSourcePage />} />
          <Route
            path="/settings/knowledge-sources"
            element={<Navigate replace to="/knowledge-sources" />}
          />
          <Route path="/knowledge-sources/:ks_id" element={<KnowledgeSourceDetailsPage />} />
          <Route path="/settings/ks-details/:ks_id" element={<KnowledgeSourceDetailsPage />} />
          <Route
            path="/knowledge-source-configurations"
            element={<KnowledgeSourceConfigurationsList />}
          />
          <Route
            path="/knowledge-source-configurations/create"
            element={<CreateKscPage />}
          />
          <Route path="/integrations" element={<IntegrationsList />} />
          <Route path="/integrations/browse" element={<IntegrationsBrowsePage />} />
          <Route path="/integrations/browse/:integration_id" element={<IntegrationsBrowsePage />} />
          <Route
            path="/integrations/api-keys"
            element={
              <FeatureFlagGuard flagKey="customer_api_enabled" redirectTo="/integrations">
                <IntegrationsApiKeysPage />
              </FeatureFlagGuard>
            }
          />
          <Route path="/integrations/:integration_id" element={<IntegrationDetailsPage />} />
          <Route
            path="/knowledge-source-configurations/:ksc_id"
            element={<KnowledgeSourceConfigurationDetailsPage />}
          />
          <Route
            path="/settings/knowledge-source-configurations"
            element={<Navigate replace to="/knowledge-source-configurations" />}
          />
          <Route
            path="/settings/ksc-details/:ksc_id"
            element={<KnowledgeSourceConfigurationDetailsPage />}
          />
          </Routes>
        </SalesforceEmbeddedRouteGuard>
      </Suspense>
    </BrowserRouter>
  );
}

export default Router;
